<template>
  <div
    :class="[
      'relative flex items-center justify-center w-full outline-none group sm:rounded-xl bg-slate-100 dark:bg-zinc-800',
      type !== ItemType.PDF && 'content-wrapper',
      type === ItemType.VIDEO && 'content-wrapper--video',
      type === ItemType.PDF ? 'overflow-hidden sm:overflow-visible' : 'overflow-hidden',
    ]"
    :style="{
      maxHeight: type === ItemType.IMAGE ? props.maxHeight : undefined,
      height: type === ItemType.IMAGE ? props.maxHeight : undefined,
      '--viewer-media-ratio': type === ItemType.VIDEO ? videoAspectRatio : undefined,
    }"
  >
    <div class="w-full h-full text-center flex justify-center items-center" @mouseenter="handleHover(true)" @mouseleave="handleHover(false)">
      <template v-if="canViewItem && contentUrl && !limitReached">
        <Image v-if="type === ItemType.IMAGE" :src="annotate.image || contentUrl" :alt="displayName" :max-height="props.maxHeight" />
        <Pdf v-else-if="type === ItemType.PDF" :src="contentUrl" />
        <Audio v-else-if="type === ItemType.AUDIO" :src="contentUrl" />
        <template v-else-if="type === ItemType.VIDEO">
          <FloatingBarAI />
          <Video :src="contentUrl" @aspect-ratio="mediaAspectRatio = $event" />
        </template>
        <Unsupported v-else :src="contentUrl" />
        <ContentCollectionNav />

        <div id="cta-wrapper" v-show="!!hoverableButtonVisible || showHoverableButton">
          <CallToAction :hoverOnly="true" />
        </div>
      </template>
      <ViewsLimit v-else-if="limitReached" />
      <Skeleton v-else />
      <div id="cta-wrapper" v-show="!!hoverableButtonVisible">
        <CallToAction :hoverOnly="true" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Skeleton from './ContentSkeleton.vue';
import { useViewerItemStore, ItemType } from './../store/useViewerItemStore';
import Image from './content_types/Image.vue';
import Pdf from './content_types/Pdf.vue';
import Video from './content_types/Video.vue';
import Audio from './content_types/Audio.vue';
import Unsupported from './content_types/Unsupported.vue';
import CallToAction from './CallToAction.vue';
import ViewsLimit from './limitations/ViewsLimit.vue';
import FloatingBarAI from './FloatingBarAI.vue';

import { useCallToActionStore } from './../store/useCallToActionStore';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import ContentCollectionNav from './ContentCollectionNav.vue';

const props = defineProps<{
  maxHeight?: string;
}>();

const viewerItemStore = useViewerItemStore();
const ctaStore = useCallToActionStore();
const { hoverableButtonVisible, showHoverableButton } = storeToRefs(ctaStore);
const { id, type, contentUrl, displayName, limitReached, annotate, canViewItem, posterUrl } = storeToRefs(viewerItemStore);
const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;
const mediaAspectRatio = ref<number | null>(null);
const posterAspectRatio = ref<number | null>(null);
const videoAspectRatio = computed(() => mediaAspectRatio.value ?? posterAspectRatio.value ?? DEFAULT_VIDEO_ASPECT_RATIO);

// Keyed on the item, not contentUrl: the store re-sets contentUrl for the same
// item (cache-buster, update deferred until pause), and HLS playback does not
// reload on it, so no new loadedmetadata would restore the ratio.
watch(id, () => {
  mediaAspectRatio.value = null;
});

// The player uses preload "none", so loadedmetadata waits for play. The poster
// gives the ratio before that.
watch(
  [posterUrl, type],
  ([url, itemType]) => {
    posterAspectRatio.value = null;
    if (!url || itemType !== ItemType.VIDEO) {
      return;
    }

    const poster = new window.Image();
    poster.onload = () => {
      if (url !== posterUrl.value || !poster.naturalWidth || !poster.naturalHeight) {
        return;
      }
      posterAspectRatio.value = poster.naturalWidth / poster.naturalHeight;
    };
    poster.src = url;
  },
  { immediate: true }
);

const handleHover = (value: boolean) => {
  if (viewerItemStore.type !== ItemType.VIDEO) {
    ctaStore.ctaHoverableButtonVisible(value);
  }
};
</script>

<style scoped>
/* The box takes the media's own ratio and is capped by the viewport height, so
   Video.js (fill + object-fit: contain) never letterboxes inside it. A layout
   raises --viewer-video-reserved-height to keep room below the player. */
.content-wrapper--video {
  aspect-ratio: var(--viewer-media-ratio);
  width: min(100%, calc(max(16rem, 100vh - var(--viewer-video-reserved-height, 7.5rem)) * var(--viewer-media-ratio)));
  width: min(100%, calc(max(16rem, 100dvh - var(--viewer-video-reserved-height, 7.5rem)) * var(--viewer-media-ratio)));
  margin-inline: auto;
}

/* A portrait box can be narrower than the AI toast, and the wrapper clips
   overflow, so the toast wraps inside the box instead. */
.content-wrapper--video :deep(.floating-bar) {
  width: max-content;
  min-width: 0;
  max-width: calc(100% - 1rem);
  border-radius: 1rem;
}

.content-wrapper--video :deep(.floating-bar .message-wrapper) {
  min-width: 0;
  text-align: left;
}

.content-wrapper--video :deep(.floating-bar .message) {
  flex-shrink: 1;
}
</style>
